import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Calendar, Loader2 } from "lucide-react";
import { createContact, createConsultation } from "../lib/api";
import { motion } from "framer-motion";

export default function Contact() {
  const [contactForm, setContactForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [bookForm, setBookForm] = useState({
    name: "", email: "", phone: "",
    date_of_birth: "", time_of_birth: "", place_of_birth: "",
    preferred_date: "", concerns: "",
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [bookLoading, setBookLoading] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await createContact(contactForm);
      toast.success("Message sent. We will respond within 24 hours.");
      setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Failed to send message."); }
    finally { setContactLoading(false); }
  };

  const submitBook = async (e) => {
    e.preventDefault();
    setBookLoading(true);
    try {
      await createConsultation(bookForm);
      toast.success("Consultation request received. Our team will confirm shortly.");
      setBookForm({ name: "", email: "", phone: "", date_of_birth: "", time_of_birth: "", place_of_birth: "", preferred_date: "", concerns: "" });
    } catch { toast.error("Booking failed. Please try again."); }
    finally { setBookLoading(false); }
  };

  const input = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition";
  const label = "text-xs tracking-widest uppercase text-[#0EA5E9] mb-1.5 block";

  return (
    <div data-testid="page-contact">
      <section className="pt-16 pb-16">
        <div className="container-page">
          <div className="eyebrow mb-4">Get in touch</div>
          <h1 className="text-5xl md:text-6xl leading-tight max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Consultation & Contact</h1>
          <p className="mt-6 text-lg text-[#475569] max-w-2xl">Book a consultation with Dr. Manish Misra or send us a general enquiry.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-page grid lg:grid-cols-3 gap-8">
          {/* Info */}
          <div className="space-y-6 lg:sticky lg:top-28 self-start">
            <div className="glass-card p-6">
              <MapPin size={20} className="text-[#D4AF37] mb-3" />
              <div className={label}>Clinic</div>
              <div className="text-[#0F172A] leading-relaxed">
                Dr. Manish Homeopathic Clinic<br />
                Shop No. 6, Ground Floor, LDA Complex<br />
                Vishwas Khand-3, Gomti Nagar<br />
                Lucknow – 226010, U.P., India
              </div>
            </div>
            <div className="glass-card p-6">
              <Phone size={20} className="text-[#D4AF37] mb-3" />
              <div className={label}>WhatsApp / Mobile</div>
              <div className="text-[#0F172A]">
                <a href="tel:+919559093555" className="hover:text-[#0EA5E9]">+91 9559093555</a>
              </div>
            </div>
            <div className="glass-card p-6">
              <Mail size={20} className="text-[#D4AF37] mb-3" />
              <div className={label}>Consultation Hours</div>
              <div className="text-[#0F172A] text-sm leading-relaxed">By appointment · Contact via WhatsApp to schedule</div>
            </div>
          </div>

          {/* Booking */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} id="book" className="lg:col-span-2 rounded-3xl bg-[#0F172A] p-8 md:p-12 text-white">
            <div className="flex items-center gap-3 mb-6">
              <Calendar size={22} className="text-[#D4AF37]" />
              <h2 className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", color: "white" }}>Book a Consultation</h2>
            </div>
            <p className="text-white/70 text-sm mb-8">Personalized 60-minute session. In-person or virtual. Birth details enable astrological chart preparation.</p>

            <form onSubmit={submitBook} className="grid md:grid-cols-2 gap-4" data-testid="booking-form">
              <div>
                <label className={label + " !text-[#D4AF37]"}>Full Name *</label>
                <input required value={bookForm.name} onChange={(e) => setBookForm({ ...bookForm, name: e.target.value })} data-testid="book-name" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} />
              </div>
              <div>
                <label className={label + " !text-[#D4AF37]"}>Email *</label>
                <input type="email" required value={bookForm.email} onChange={(e) => setBookForm({ ...bookForm, email: e.target.value })} data-testid="book-email" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} />
              </div>
              <div>
                <label className={label + " !text-[#D4AF37]"}>Phone *</label>
                <input required value={bookForm.phone} onChange={(e) => setBookForm({ ...bookForm, phone: e.target.value })} data-testid="book-phone" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} />
              </div>
              <div>
                <label className={label + " !text-[#D4AF37]"}>Preferred Date *</label>
                <input type="date" required value={bookForm.preferred_date} onChange={(e) => setBookForm({ ...bookForm, preferred_date: e.target.value })} data-testid="book-date" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} />
              </div>
              <div>
                <label className={label + " !text-[#D4AF37]"}>Date of Birth</label>
                <input type="date" value={bookForm.date_of_birth} onChange={(e) => setBookForm({ ...bookForm, date_of_birth: e.target.value })} data-testid="book-dob" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} />
              </div>
              <div>
                <label className={label + " !text-[#D4AF37]"}>Time of Birth</label>
                <input type="time" value={bookForm.time_of_birth} onChange={(e) => setBookForm({ ...bookForm, time_of_birth: e.target.value })} data-testid="book-tob" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} />
              </div>
              <div className="md:col-span-2">
                <label className={label + " !text-[#D4AF37]"}>Place of Birth</label>
                <input value={bookForm.place_of_birth} onChange={(e) => setBookForm({ ...bookForm, place_of_birth: e.target.value })} data-testid="book-pob" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} placeholder="City, Country" />
              </div>
              <div className="md:col-span-2">
                <label className={label + " !text-[#D4AF37]"}>Health Concerns *</label>
                <textarea required rows={4} value={bookForm.concerns} onChange={(e) => setBookForm({ ...bookForm, concerns: e.target.value })} data-testid="book-concerns" className={input + " bg-white/5 border-white/10 text-white focus:border-[#D4AF37]"} />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={bookLoading} data-testid="book-submit" className="btn-gold w-full disabled:opacity-60">
                  {bookLoading ? <Loader2 className="animate-spin" size={18} /> : <>Request Consultation <Send size={16} className="ml-2" /></>}
                </button>
              </div>
            </form>
          </motion.div>
        </div>

        {/* Contact */}
        <div className="container-page mt-16">
          <div className="max-w-3xl mx-auto rounded-3xl border border-slate-100 bg-white p-8 md:p-12">
            <h2 className="text-3xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>General Enquiry</h2>
            <p className="text-[#475569] text-sm mb-8">For questions, partnerships, or media requests.</p>
            <form onSubmit={submitContact} className="grid md:grid-cols-2 gap-4" data-testid="contact-form">
              <div>
                <label className={label}>Name *</label>
                <input required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} data-testid="contact-name" className={input} />
              </div>
              <div>
                <label className={label}>Email *</label>
                <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} data-testid="contact-email" className={input} />
              </div>
              <div className="md:col-span-2">
                <label className={label}>Subject *</label>
                <input required value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} data-testid="contact-subject" className={input} />
              </div>
              <div className="md:col-span-2">
                <label className={label}>Message *</label>
                <textarea required rows={4} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} data-testid="contact-message" className={input} />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={contactLoading} data-testid="contact-submit" className="btn-sky w-full disabled:opacity-60">
                  {contactLoading ? <Loader2 className="animate-spin" size={18} /> : <>Send message <Send size={16} className="ml-2" /></>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
